<script setup lang="ts">
/**
 * What a tap on the usage strip opens: each of its meters as a chart over the
 * last 6 hours to 30 days (server/systemStats.ts `statsHistory`), one chart per
 * measure, stacked on one time axis. Pointing at any of them reads out every
 * chart at that moment, since the question is usually "what else was going on".
 *
 * Under the 5-hour chart is what was USED at each point: how far the window
 * rose since the point before (`used`, summed by the server from
 * `fiveHourRise`), so a reset's fall to nothing is not drawn as anything. It
 * is the one chart scaled to its own tallest point rather than to 100%.
 *
 * The 5-hour chart marks, along its foot, where its rises happened while
 * nothing on the box used Claude: the plan being spent somewhere else.
 *
 * Last, the tmux sessions on the box at each point: a count, scaled to its
 * tallest like an amount, with its peak behind it like CPU's.
 *
 * Under those, the box's tokens at each point, stacked by the repo its Claudes
 * worked in, with use elsewhere on top (`tokenStack()`, lib/tokenStack.ts). Its
 * legend is its readout: each band's tokens at the moment pointed at, else over
 * the whole range. Pointing at a legend entry (a tap, on a phone, until the next)
 * lights its band and fades the rest, in the chart and the legend.
 *
 * An admin also gets a button on the Disk, CPU and RAM headings that opens what
 * is taking it, under the chart: DiskBreakdown, ProcessBreakdown.
 *
 * Above the charts, the Claude usage as it stands now: its state
 * (`usageState()`) and the figures behind it (`usageDetails()`) — what the
 * strip's colours and state name only summarise.
 *
 * An admin's way to Global settings sits at the top, above all of that.
 *
 * Opening pushes a history entry, so a phone's back closes it.
 */
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { api } from "../lib/api.js";
import { usePoll } from "../lib/poll.js";
import { USAGE_STATE_LABELS, USAGE_STATE_TITLES, claudeUsage, loadClaudeUsage, usageDetails } from "../lib/claudeUsage.js";
import { formatTokens } from "../lib/format.js";
import { tokenStack, type TokenSeries } from "../lib/tokenStack.js";
import { usageState } from "@shared/usage.js";
import { useBoard } from "../stores/board.js";
import { openWindow } from "../lib/windows.js";
import DiskBreakdown from "./DiskBreakdown.vue";
import ProcessBreakdown from "./ProcessBreakdown.vue";
import { HOUR_MS, STATS_RANGES, type StatsHistory } from "@shared/types.js";

const emit = defineEmits<{ close: [] }>();
const board = useBoard();

/** The chart whose breakdown is open under it, one at a time. */
const detail = ref<string | null>(null);
/** The button each chart's heading carries, for an admin. */
const DETAIL_LABEL: Record<string, string> = { disk: "Largest files", cpu: "Top processes", ram: "Top processes" };
const detailLabel = (key: string): string | null => (board.me?.isAdmin ? (DETAIL_LABEL[key] ?? null) : null);

const MARK = "todosStats";
const RANGE_KEY = "todos.statsRange";
const REFRESH_MS = 60_000;

function savedRange(): number {
  try {
    const n = Number(localStorage.getItem(RANGE_KEY));
    return (STATS_RANGES as readonly number[]).includes(n) ? n : 24;
  } catch {
    return 24;
  }
}

const hours = ref<number>(savedRange());
const stats = ref<StatsHistory | null>(null);
const failed = ref(false);

watch(hours, (h) => {
  try {
    localStorage.setItem(RANGE_KEY, String(h));
  } catch {
    // Private window: this tab only.
  }
  void load();
});

let loading = 0;
async function load(): Promise<void> {
  const ask = ++loading;
  try {
    const h = (await api.get<{ history: StatsHistory }>(`/api/system-stats/history?hours=${hours.value}`)).history;
    if (ask !== loading) return;
    stats.value = h;
    failed.value = false;
  } catch {
    if (ask === loading) failed.value = true;
  }
}

// ── the usage now ──────────────────────────────────────────────────────────

/** Ticks, so "resets in" and the state move while the view is open. */
const nowMs = ref(Date.now());
const usageNow = computed(() => usageState(claudeUsage.value, nowMs.value));
const details = computed(() => usageDetails(claudeUsage.value, nowMs.value));

const rangeLabel = (h: number): string => (h < 24 ? `${h}h` : `${h / 24}d`);

// ── the charts ─────────────────────────────────────────────────────────────

type Point = { at: number; v: number };
interface Chart {
  key: string;
  label: string;
  kind: "claude" | "system";
  points: Point[];
  /** A fainter area behind the line: CPU's per-bucket peak. */
  peak: Point[] | null;
  /** Longer than this between points breaks the line: nothing was recorded. */
  gapMs: number;
  /** Points of rise put down to use off the box, per bucket that had any. */
  outside?: Point[];
  /** An amount per point, not a level: scaled to its tallest, read out with a "+". */
  amount?: boolean;
  /** A count, not a percentage: scaled to its tallest, read out as a whole number. */
  count?: boolean;
}

const series = <T extends { at: number }>(rows: T[], pick: (r: T) => number | null): Point[] =>
  rows.flatMap((r) => {
    const v = pick(r);
    return v === null || !Number.isFinite(v) ? [] : [{ at: r.at, v }];
  });

/** How often the runner reports (RUNNER_USAGE_MS): what a point of "used" spans at the least. */
const USAGE_EVERY_MS = 5 * 60_000;

function spanLabel(ms: number): string {
  const min = Math.round(ms / 60_000);
  return min < 60 ? `${min} min` : `${Math.round(ms / HOUR_MS)} h`;
}

const charts = computed<Chart[]>(() => {
  const h = stats.value;
  if (!h) return [];
  // The runner reports every five minutes, the box is sampled every minute.
  const usageGap = Math.max(3 * h.bucketMs, 16 * 60_000);
  const systemGap = Math.max(3 * h.bucketMs, 4 * 60_000);
  return [
    {
      key: "five",
      label: "Claude, 5-hour",
      kind: "claude",
      points: series(h.usage, (r) => r.fiveHour),
      peak: null,
      gapMs: usageGap,
      outside: series(h.usage, (r) => (r.outside > 0 ? r.outside : null)),
    },
    {
      key: "used",
      label: `Claude, 5-hour used per ${spanLabel(Math.max(h.bucketMs, USAGE_EVERY_MS))}`,
      kind: "claude",
      points: series(h.usage, (r) => r.used),
      peak: null,
      gapMs: usageGap,
      amount: true,
    },
    { key: "week", label: "Claude, weekly", kind: "claude", points: series(h.usage, (r) => r.weekly), peak: null, gapMs: usageGap },
    { key: "disk", label: "Disk used", kind: "system", points: series(h.system, (r) => r.disk), peak: null, gapMs: systemGap },
    {
      key: "cpu",
      label: "CPU busy",
      kind: "system",
      points: series(h.system, (r) => r.cpu),
      // A bucket of one sample has nothing to show above its line.
      peak: h.bucketMs > 60_000 ? series(h.system, (r) => r.cpuMax) : null,
      gapMs: systemGap,
    },
    { key: "ram", label: "RAM used", kind: "system", points: series(h.system, (r) => r.ram), peak: null, gapMs: systemGap },
    {
      key: "tmux",
      label: "tmux sessions",
      kind: "system",
      points: series(h.system, (r) => r.tmux),
      peak: h.bucketMs > 60_000 ? series(h.system, (r) => r.tmuxMax) : null,
      gapMs: systemGap,
      count: true,
    },
  ];
});

const W = 600;
const H = 80;

/** 100%, in steps of 50 should a percentage pass it. An amount or a count
 *  fits its tallest point instead, rounded up to 1, 2 or 5 times a power of ten. */
function yMax(c: Chart): number {
  const most = Math.max(0, ...(c.peak ?? c.points).map((p) => p.v));
  if (!c.amount && !c.count) return Math.max(100, Math.ceil(most / 50) * 50);
  const pow = 10 ** Math.floor(Math.log10(Math.max(most, 1)));
  return [1, 2, 5, 10].map((m) => m * pow).find((top) => top >= most) ?? 10 * pow;
}

function x(at: number): number {
  const h = stats.value!;
  return ((at - h.from) / (h.to - h.from)) * W;
}

/** Runs of points with no gap in them. */
function runs(points: Point[], gapMs: number): Point[][] {
  const out: Point[][] = [];
  for (const p of points) {
    const run = out[out.length - 1];
    if (run && p.at - run[run.length - 1]!.at <= gapMs) run.push(p);
    else out.push([p]);
  }
  return out;
}

function linePath(c: Chart, points: Point[]): string {
  const top = yMax(c);
  const y = (v: number): number => H - (Math.min(v, top) / top) * H;
  return runs(points, c.gapMs)
    .map((run) => {
      // A lone point still shows, as a short tick.
      const pts = run.length === 1 ? [run[0]!, { at: run[0]!.at + c.gapMs / 6, v: run[0]!.v }] : run;
      return pts.map((p, i) => `${i ? "L" : "M"}${x(p.at).toFixed(1)},${y(p.v).toFixed(1)}`).join("");
    })
    .join("");
}

function areaPath(c: Chart, points: Point[]): string {
  const top = yMax(c);
  const y = (v: number): number => H - (Math.min(v, top) / top) * H;
  return runs(points, c.gapMs)
    .filter((run) => run.length > 1)
    .map((run) => {
      const line = run.map((p) => `L${x(p.at).toFixed(1)},${y(p.v).toFixed(1)}`).join("");
      return `M${x(run[0]!.at).toFixed(1)},${H}${line}L${x(run[run.length - 1]!.at).toFixed(1)},${H}Z`;
    })
    .join("");
}

// ── tokens per repo ────────────────────────────────────────────────────────

const stack = computed(() => (stats.value ? tokenStack(stats.value) : null));
const usageGapMs = computed(() => Math.max(3 * (stats.value?.bucketMs ?? 0), 16 * 60_000));

/** The tallest stack, rounded up to 1, 2 or 5 times a power of ten. */
const stackTop = computed(() => {
  const most = Math.max(1, ...(stack.value?.totals ?? []));
  const pow = 10 ** Math.floor(Math.log10(most));
  return [1, 2, 5, 10].map((m) => m * pow).find((top) => top >= most) ?? 10 * pow;
});

/** Each band's outline and fill. A band is drawn only between points where it has something. */
const bands = computed(() => {
  const st = stack.value;
  if (!st) return [];
  const top = stackTop.value;
  const gap = usageGapMs.value;
  const y = (v: number): string => (H - (Math.min(v, top) / top) * H).toFixed(1);
  // Runs of point indexes with no gap between them; a lone point is given a little width.
  const spans: { at: number; i: number }[][] = [];
  st.at.forEach((at, i) => {
    const run = spans[spans.length - 1];
    if (run && at - run[run.length - 1]!.at <= gap) run.push({ at, i });
    else spans.push([{ at, i }]);
  });
  for (const run of spans) if (run.length === 1) run.push({ at: run[0]!.at + gap / 6, i: run[0]!.i });

  const under = st.at.map(() => 0);
  return st.series.map((s) => {
    const low = [...under];
    s.values.forEach((v, i) => (under[i]! += v));
    const high = [...under];
    let line = "";
    let area = "";
    // One shape per stretch of the band, so its fill has no seams: along its top, back along its foot.
    const draw = (pts: { at: number; i: number }[]): void => {
      if (pts.length < 2) return;
      const upper = pts.map((p, k) => `${k ? "L" : "M"}${x(p.at).toFixed(1)},${y(high[p.i]!)}`).join("");
      line += upper;
      area += `${upper}${[...pts].reverse().map((p) => `L${x(p.at).toFixed(1)},${y(low[p.i]!)}`).join("")}Z`;
    };
    for (const run of spans) {
      let pts: { at: number; i: number }[] = [];
      for (let k = 0; k + 1 < run.length; k++) {
        const a = run[k]!;
        const b = run[k + 1]!;
        if (s.values[a.i] || s.values[b.i]) {
          if (!pts.length) pts.push(a);
          pts.push(b);
        } else {
          draw(pts);
          pts = [];
        }
      }
      draw(pts);
    }
    return { key: s.key, slot: s.slot, line, area };
  });
});

/** The point of the stack nearest the hovered moment, within a gap of it; null reads out the range. */
const stackIndex = computed<number | null>(() => {
  const st = stack.value;
  const at = hoverAt.value;
  if (!st || at === null) return null;
  let best = -1;
  st.at.forEach((t, i) => {
    if (best < 0 || Math.abs(t - at) < Math.abs(st.at[best]! - at)) best = i;
  });
  return best >= 0 && Math.abs(st.at[best]! - at) <= usageGapMs.value ? best : null;
});

/** The band whose legend entry is pointed at; else the one tapped, which stays lit until tapped again. */
const hoveredBand = ref<string | null>(null);
const pinnedBand = ref<string | null>(null);
const litBand = computed(() => hoveredBand.value ?? pinnedBand.value);

function bandClass(key: string): Record<string, boolean> {
  const lit = litBand.value;
  return { lit: lit === key, faded: lit !== null && lit !== key };
}

/** A mouse lights a band while over its entry; a touch's enter and leave are its tap, which pins it instead. */
function pointBand(ev: PointerEvent, key: string | null): void {
  if (ev.pointerType !== "touch") hoveredBand.value = key;
}

function pinBand(key: string): void {
  pinnedBand.value = pinnedBand.value === key ? null : key;
}

const stackTotal = computed(() => (stack.value?.totals ?? []).reduce((a, b) => a + b, 0));

/** What the legend says of a band: at the point hovered, else over the range, with its share. */
function bandValue(s: TokenSeries): string {
  const approx = s.estimate ? "≈" : "";
  const i = stackIndex.value;
  if (hoverAt.value !== null) return i === null ? "–" : approx + formatTokens(s.values[i]!);
  const share = stackTotal.value > 0 ? ` · ${pct((100 * s.total) / stackTotal.value)}` : "";
  return approx + formatTokens(s.total) + share;
}

// ── reading them ───────────────────────────────────────────────────────────

/** The moment pointed at, shared by every chart; null reads out the latest. */
const hoverAt = ref<number | null>(null);

function onPointer(ev: PointerEvent): void {
  const h = stats.value;
  if (!h) return;
  const box = (ev.currentTarget as HTMLElement).getBoundingClientRect();
  const f = Math.min(1, Math.max(0, (ev.clientX - box.left) / box.width));
  hoverAt.value = h.from + f * (h.to - h.from);
}

/** The point nearest the hovered moment, within a gap of it; else the latest. */
function readout(c: Chart): Point | null {
  if (!c.points.length) return null;
  const at = hoverAt.value;
  if (at === null) return c.points[c.points.length - 1]!;
  let best: Point | null = null;
  for (const p of c.points) if (!best || Math.abs(p.at - at) < Math.abs(best.at - at)) best = p;
  return best && Math.abs(best.at - at) <= c.gapMs ? best : null;
}

function peakAt(c: Chart, at: number): number | null {
  return c.peak?.find((p) => p.at === at)?.v ?? null;
}

function outsideAt(c: Chart, at: number): number | null {
  return c.outside?.find((p) => p.at === at)?.v ?? null;
}

/** Every rise put down to use elsewhere over the range, for the note under the charts. */
const outsideTotal = computed(() => (stats.value?.usage ?? []).reduce((n, r) => n + r.outside, 0));

const pct = (v: number): string => `${v > 0 && v < 1 ? "<1" : Math.round(v)}%`;
/** A chart's level as its caption reads it: a count whole, else a percentage. */
const level = (c: Chart, v: number): string => (c.count ? String(Math.round(v)) : pct(v));
/** A chart's value as its caption reads it: a level, or an amount added. */
const shown = (c: Chart, v: number): string => (c.amount && v > 0 ? `+${pct(v)}`.replace("+<", "<") : level(c, v));

const timeFmt = new Intl.DateTimeFormat(undefined, { hour: "2-digit", minute: "2-digit" });
const dayFmt = new Intl.DateTimeFormat(undefined, { weekday: "short", hour: "2-digit", minute: "2-digit" });
const dateFmt = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" });

const when = (at: number): string => (hours.value <= 24 ? timeFmt.format(at) : dayFmt.format(at));

/** Five labels along the shared axis. */
const ticks = computed(() => {
  const h = stats.value;
  if (!h) return [];
  return [0, 0.25, 0.5, 0.75, 1].map((f) => {
    const at = h.from + f * (h.to - h.from);
    return { f, label: hours.value <= 24 ? timeFmt.format(at) : hours.value <= 24 * 7 ? dayFmt.format(at) : dateFmt.format(at) };
  });
});

const hoverLeft = computed(() => {
  const h = stats.value;
  return h && hoverAt.value !== null ? ((hoverAt.value - h.from) / (h.to - h.from)) * 100 : null;
});

const bucketNote = computed(() => {
  const ms = stats.value?.bucketMs ?? 0;
  if (ms <= 60_000) return "";
  return `each point averages ${spanLabel(ms)}`;
});

// ── open and close ─────────────────────────────────────────────────────────

function closed(): void {
  emit("close");
}

function close(): void {
  // Undo our own push; the popstate then closes the view.
  if (window.history.state?.[MARK]) window.history.back();
  else closed();
}

/** Global settings is a window over the view under this modal: close it to get there. */
function openGlobalSettings(): void {
  openWindow({ kind: "settings" });
  close();
}

function onPopState(): void {
  if (!window.history.state?.[MARK]) closed();
}

function onKeydown(ev: KeyboardEvent): void {
  if (ev.key === "Escape") close();
}

let clock = 0;
// The history, and the usage now (shared with the strip: lib/claudeUsage.ts).
usePoll(async () => {
  await Promise.all([load(), loadClaudeUsage()]);
  return !failed.value;
}, REFRESH_MS);
onMounted(() => {
  clock = window.setInterval(() => (nowMs.value = Date.now()), 15_000);
  window.history.pushState({ ...window.history.state, todosPushed: true, [MARK]: true }, "");
  window.addEventListener("popstate", onPopState);
  window.addEventListener("keydown", onKeydown);
});
onBeforeUnmount(() => {
  window.removeEventListener("popstate", onPopState);
  window.removeEventListener("keydown", onKeydown);
  window.clearInterval(clock);
});
</script>

<template>
  <Teleport to="body">
    <div class="modal-backdrop" @pointerdown.self="close">
      <section class="modal stats-modal" role="dialog" aria-modal="true" aria-labelledby="stats-title">
        <header class="modal-head">
          <span id="stats-title">Usage &amp; server</span>
          <span style="flex: 1"></span>
          <div class="stats-ranges" role="radiogroup" aria-label="Range">
            <button
              v-for="h in STATS_RANGES"
              :key="h"
              type="button"
              class="btn ghost"
              :class="{ on: hours === h }"
              role="radio"
              :aria-checked="hours === h"
              @click="hours = h"
            >
              {{ rangeLabel(h) }}
            </button>
          </div>
          <button class="btn ghost" type="button" title="Close" aria-label="Close" @click="close">✕</button>
        </header>

        <div class="modal-body stats-body">
          <p v-if="board.me?.isAdmin" class="stats-settings">
            <button class="btn" type="button" @click="openGlobalSettings">Global settings ›</button>
          </p>
          <section v-if="details.length" class="stats-now" aria-label="Claude usage now">
            <p class="stats-now-state">
              <span class="stats-now-chip" :class="`usage-state-${usageNow}`">{{ USAGE_STATE_LABELS[usageNow] }}</span>
              <span class="stats-now-why">{{ USAGE_STATE_TITLES[usageNow].replace(/^[^:]*: /, "") }}</span>
            </p>
            <dl class="stats-now-grid">
              <div v-for="d in details" :key="d.key" class="stats-now-item">
                <dt>{{ d.label }}</dt>
                <dd class="stats-now-value" :class="d.tone ? `tone-${d.tone}` : undefined">{{ d.value }}</dd>
                <dd class="stats-now-sub">{{ d.sub }}</dd>
              </div>
            </dl>
          </section>
          <p v-if="failed && !stats" class="modal-lede">Could not load the history.</p>
          <p v-else-if="!stats" class="modal-lede">Loading…</p>
          <template v-else>
            <div class="stats-plots">
              <template v-for="c in charts" :key="c.key">
              <figure class="stats-chart" :class="c.kind">
                <figcaption class="stats-caption">
                  <span class="stats-label">{{ c.label }}</span>
                  <span class="stats-value">
                    <template v-if="readout(c)">
                      {{ shown(c, readout(c)!.v) }}
                      <span v-if="peakAt(c, readout(c)!.at) !== null" class="stats-sub">peak {{ level(c, peakAt(c, readout(c)!.at)!) }}</span>
                      <span v-if="outsideAt(c, readout(c)!.at) !== null" class="stats-sub stats-elsewhere">+{{ pct(outsideAt(c, readout(c)!.at)!) }} elsewhere</span>
                      <span class="stats-sub">{{ hoverAt === null ? "latest" : when(readout(c)!.at) }}</span>
                    </template>
                    <span v-else class="stats-sub">{{ c.points.length ? "no reading here" : "no readings yet" }}</span>
                  </span>
                  <button
                    v-if="detailLabel(c.key)"
                    type="button"
                    class="btn ghost stats-detail-btn"
                    :class="{ on: detail === c.key }"
                    :aria-expanded="detail === c.key"
                    @click="detail = detail === c.key ? null : c.key"
                  >
                    {{ detailLabel(c.key) }}
                  </button>
                </figcaption>
                <div class="stats-plot" @pointermove="onPointer" @pointerdown="onPointer" @pointerleave="hoverAt = null">
                  <span class="stats-y top">{{ yMax(c) }}{{ c.count ? "" : "%" }}</span>
                  <svg :viewBox="`0 0 ${W} ${H}`" preserveAspectRatio="none" role="img" :aria-label="`${c.label} over the last ${rangeLabel(hours)}`">
                    <line class="stats-grid" x1="0" :x2="W" :y1="H / 2" :y2="H / 2" />
                    <line
                      v-if="!c.count && yMax(c) > 100"
                      class="stats-grid full"
                      x1="0"
                      :x2="W"
                      :y1="H - (100 / yMax(c)) * H"
                      :y2="H - (100 / yMax(c)) * H"
                    />
                    <path v-if="c.peak" class="stats-peak" :d="areaPath(c, c.peak)" />
                    <path class="stats-area" :d="areaPath(c, c.points)" />
                    <path class="stats-line" :d="linePath(c, c.points)" />
                    <rect
                      v-for="o in c.outside ?? []"
                      :key="o.at"
                      class="stats-elsewhere-mark"
                      :x="x(o.at) - 2"
                      :y="H - 6"
                      width="4"
                      height="6"
                    ><title>+{{ pct(o.v) }} used elsewhere · {{ when(o.at) }}</title></rect>
                  </svg>
                  <div v-if="hoverLeft !== null" class="stats-cross" :style="{ left: hoverLeft + '%' }"></div>
                </div>
                <div v-if="detail === c.key" class="stats-detail-wrap">
                  <DiskBreakdown v-if="c.key === 'disk'" />
                  <ProcessBreakdown v-else :key="c.key" :sort="c.key === 'ram' ? 'ram' : 'cpu'" />
                </div>
              </figure>
              <figure v-if="c.key === 'used' && stack" class="stats-chart claude stats-tokens">
                <figcaption class="stats-caption">
                  <span class="stats-label">Claude, tokens per repo per {{ spanLabel(Math.max(stats.bucketMs, USAGE_EVERY_MS)) }}</span>
                  <span class="stats-value">
                    <template v-if="!stack.at.length"><span class="stats-sub">no readings yet</span></template>
                    <template v-else-if="hoverAt === null">
                      {{ formatTokens(stackTotal) }}
                      <span class="stats-sub">over {{ rangeLabel(hours) }}</span>
                    </template>
                    <template v-else-if="stackIndex !== null">
                      {{ formatTokens(stack.totals[stackIndex]!) }}
                      <span class="stats-sub">{{ when(stack.at[stackIndex]!) }}</span>
                    </template>
                    <span v-else class="stats-sub">no reading here</span>
                  </span>
                </figcaption>
                <div class="stats-plot" @pointermove="onPointer" @pointerdown="onPointer" @pointerleave="hoverAt = null">
                  <span class="stats-y top">{{ formatTokens(stackTop) }}</span>
                  <svg :viewBox="`0 0 ${W} ${H}`" preserveAspectRatio="none" role="img" :aria-label="`Tokens per repo over the last ${rangeLabel(hours)}`">
                    <defs>
                      <pattern id="stats-hatch" width="5" height="5" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                        <line class="stats-hatch-line" x1="0" y1="0" x2="0" y2="5" />
                      </pattern>
                    </defs>
                    <line class="stats-grid" x1="0" :x2="W" :y1="H / 2" :y2="H / 2" />
                    <g v-for="b in bands" :key="b.key" class="stats-band" :class="[b.slot, bandClass(b.key)]">
                      <path class="stats-band-area" :d="b.area" />
                      <path class="stats-line" :d="b.line" />
                    </g>
                  </svg>
                  <div v-if="hoverLeft !== null" class="stats-cross" :style="{ left: hoverLeft + '%' }"></div>
                </div>
                <ul v-if="stack.series.length" class="stats-legend">
                  <li
                    v-for="s in stack.series"
                    :key="s.key"
                    class="stats-band"
                    :class="[s.slot, bandClass(s.key)]"
                    @pointerenter="pointBand($event, s.key)"
                    @pointerleave="pointBand($event, null)"
                    @click="pinBand(s.key)"
                  >
                    <span class="stats-swatch" aria-hidden="true"></span>
                    <span class="stats-legend-name">{{ s.label }}</span>
                    <span class="stats-legend-value">{{ bandValue(s) }}</span>
                  </li>
                </ul>
              </figure>
              </template>
              <div class="stats-axis">
                <span v-for="t in ticks" :key="t.f" :style="{ left: t.f * 100 + '%' }" :class="{ first: t.f === 0, last: t.f === 1 }">{{ t.label }}</span>
              </div>
            </div>
            <p class="stats-note">
              CPU is every core's busy share over each minute, so 100% is all of them flat out. RAM counts page cache as free. tmux sessions are every session on the box's bridge, Claudes, deploys and shells alike.
              <template v-if="bucketNote"> {{ bucketNote }}.</template>
              5-hour used is how far the window rose at each point, added up rather than averaged; when it resets, only what the new window
              already holds counts.
              <template v-if="stack?.series.length">
                Tokens per repo are what the Claudes on this box wrote to their transcripts while working there: input, cache writes and output,
                cache reads left out.
                <template v-if="stack.rate !== null && outsideTotal > 0">
                  Elsewhere is an estimate: its share of the 5-hour window, at the {{ formatTokens(stack.rate) }} tokens a point of it cost this
                  box over this range.
                </template>
              </template>
              <template v-if="outsideTotal > 0">
                Marks under the 5-hour chart are rises while nothing on this box used Claude — {{ pct(outsideTotal) }} in all over this range, spent
                on claude.ai, the app or another machine on the same account.
              </template>
            </p>
          </template>
        </div>
      </section>
    </div>
  </Teleport>
</template>
